import { useEffect, useState } from 'react';
import { Box, Typography, Button, IconButton, Stack, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useProject } from '../../api/hooks/useProjects';
import { ProjectInfoCard } from './components/ProjectInfoCard';
import { ParticipantSection } from './components/ParticipantSection';
import { KanbanTab } from './components/kanban/KanbanTab';
import { ProzesslandkartePage } from '../prozesslandkarte/ProzesslandkartePage';
import { ProjectFormDialog } from './components/ProjectFormDialog';
import { AnforderungenTab } from './tabs/AnforderungenTab';
import { StakeholdersTab } from './tabs/StakeholdersTab';
import { RisikenTab } from './tabs/RisikenTab';
import { MeetingsTab } from './tabs/MeetingsTab';
import { OffenePunkteTab } from './tabs/OffenePunkteTab';
import { TeamTab } from './tabs/TeamTab';
import { RetroTab } from './tabs/RetroTab';
import { ArbeitspaketeTab } from './tabs/ArbeitspaketeTab';
import { DashboardTab } from './tabs/DashboardTab';
import { MachbarkeitTab } from './tabs/MachbarkeitTab';
import { ProjektauftragTab } from './tabs/ProjektauftragTab';
import { ZieleTab } from './tabs/ZieleTab';
import { ZeitenTab } from './tabs/ZeitenTab';
import { ScopeStatementTab } from './tabs/ScopeStatementTab';
import { ProjektabschlussTab } from './tabs/ProjektabschlussTab';
import { BurnUpTab } from './tabs/BurnUpTab';
import { StatusReportTab } from './tabs/StatusReportTab';
import { ProjektplanTab } from './tabs/ProjektplanTab';
import { ExportMenuButton } from '../../components/export/ExportMenuButton';
import { RequirePermission } from '../../components/common/RequirePermission';
import { PROJECT_PERMISSIONS } from '@pm/shared';
import { PROJECT_DEFAULT_PHASE, PROJECT_SIDEBAR_GROUPS, flattenSidebarItems } from '../../data/projectSidebarConfig';
import { useSyncActiveProjectFromUrl } from '../../context/ActiveProjectContext';

const PermissionFallback = ({ message }: { message: string }) => (
  <Box sx={{ p: 3 }}>
    <Typography color="text.secondary">{message}</Typography>
  </Box>
);

interface ProjectData {
  id: string;
  name: string;
  description: string;
  state: string;
  priority: string;
  bill_type: string;
  project_no: string;
  project_reference: string;
  deadline: string | null;
  planned_start: string | null;
  planned_end: string | null;
  project_type_id: string | null;
  link_uri: string | null;
  [key: string]: unknown;
}

const ALL_PHASE_KEYS = PROJECT_SIDEBAR_GROUPS.flatMap((g) => flattenSidebarItems(g.items).map((i) => i.key));

function isValidPhase(phase: string | undefined): boolean {
  return !!phase && ALL_PHASE_KEYS.includes(phase);
}

export function ProjectDetailPage() {
  const { t } = useTranslation('projects');
  const { id, phase: phaseParam } = useParams<{ id: string; phase?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  useSyncActiveProjectFromUrl();

  const { data, isLoading, error } = useProject(id);
  const project = (data as unknown as { data?: ProjectData } | undefined)?.data ?? null;

  const [editOpen, setEditOpen] = useState(false);

  // Default-Phase: /projects/:id ohne Suffix → Dashboard
  useEffect(() => {
    if (!id) return;
    if (!phaseParam) {
      navigate(`/projects/${id}/${PROJECT_DEFAULT_PHASE}`, { replace: true });
    } else if (!isValidPhase(phaseParam)) {
      navigate(`/projects/${id}/${PROJECT_DEFAULT_PHASE}`, { replace: true });
    }
  }, [id, phaseParam, navigate]);

  const phase = isValidPhase(phaseParam) ? phaseParam! : PROJECT_DEFAULT_PHASE;

  // PHQ-67: Beim Wechsel der Ansicht (Klick in der Navigation) oben beginnen —
  // sonst bleibt die Scroll-Position der vorigen, längeren Ansicht stehen.
  // Scrollbar kann das Fenster oder das <main> der ContentArea sein.
  useEffect(() => {
    try { window.scrollTo(0, 0); } catch { /* jsdom/Umgebung ohne scrollTo */ }
    document.querySelector('main')?.scrollTo?.(0, 0);
  }, [id, phase]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error?.message ?? t('errors.notFound', 'Projekt nicht gefunden')}</Typography>
      </Box>
    );
  }

  const denied = <PermissionFallback message={t('errors.forbidden', 'Keine Berechtigung für diesen Bereich.')} />;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/projects')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ flex: 1 }}>{project.name}</Typography>
        <ExportMenuButton projectId={project.id} projectName={project.name} />
        {phase === 'dashboard' && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
            {t('actions.edit', 'Bearbeiten')}
          </Button>
        )}
      </Stack>

      {phase === 'dashboard' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.PROJECT.READ} fallback={denied}>
          <Stack spacing={3}>
            <DashboardTab projectId={project.id} />
            <ProjectInfoCard project={project} />
            <ParticipantSection projectId={project.id} />
          </Stack>
        </RequirePermission>
      )}
      {phase === 'prozesslandkarte' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.PROJECT.READ} fallback={denied}>
          <ProzesslandkartePage projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'stakeholders' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.STAKEHOLDER.READ} fallback={denied}>
          <StakeholdersTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'machbarkeit' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.PROJECT_INITIALIZATION.READ} fallback={denied}>
          <MachbarkeitTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'projektauftrag' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.PROJECT_CONFIG.READ} fallback={denied}>
          <ProjektauftragTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'ziele' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.PROJECT_INITIALIZATION.READ} fallback={denied}>
          <ZieleTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'zeiten' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.PROJECT_INITIALIZATION.READ} fallback={denied}>
          <ZeitenTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'team' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.TEAM.READ} fallback={denied}>
          <TeamTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'anforderungen' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.ANFORDERUNG.READ} fallback={denied}>
          <AnforderungenTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'scope' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.PROJECT_CONFIG.READ} fallback={denied}>
          <ScopeStatementTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'arbeitspakete' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.ARBEITSPAKET.READ} fallback={denied}>
          <ArbeitspaketeTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'projektplan' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.TASK.READ} fallback={denied}>
          <ProjektplanTab projectId={project.id} apId={searchParams.get('ap')} />
        </RequirePermission>
      )}
      {phase === 'risiken' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.RISIKO.READ} fallback={denied}>
          <RisikenTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'meetings' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.MEETING.READ} fallback={denied}>
          <MeetingsTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'offene-punkte' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.OFFENER_PUNKT.READ} fallback={denied}>
          <OffenePunkteTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'kanban' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.TASK.READ} fallback={denied}>
          <KanbanTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'burnup' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.TASK.READ} fallback={denied}>
          <BurnUpTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'status' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.WEEKLY_PROGRESS.READ} fallback={denied}>
          <StatusReportTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'retro' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.RETRO.READ} fallback={denied}>
          <RetroTab projectId={project.id} />
        </RequirePermission>
      )}
      {phase === 'abschluss' && (
        <RequirePermission permission={PROJECT_PERMISSIONS.PROJECT_ABSCHLUSS.READ} fallback={denied}>
          <ProjektabschlussTab projectId={project.id} />
        </RequirePermission>
      )}

      <ProjectFormDialog
        open={editOpen}
        mode="edit"
        initialData={project}
        onClose={() => setEditOpen(false)}
      />
    </Box>
  );
}
